import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CsvImportTooltip.css';

interface CsvImportTooltipProps {
  variant: 'multi' | 'single';
}

const CsvImportTooltip: React.FC<CsvImportTooltipProps> = ({ variant }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  const exampleCsv = variant === 'multi'
    ? `"product_code","serial_number","quantity"\n"2719359TS002","701M12010","2"\n"7101001005","701M12010","3"`
    : `"product_code","quantity"\n"2719359TS002","2"\n"7101001005","3"`;

  return (
    <div className="status-flow-tooltip-container">
      <button
        ref={buttonRef}
        type="button"
        className="status-flow-trigger"
        onClick={() => setIsVisible(!isVisible)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        aria-label={t('csvImportInfo.title')}
        aria-expanded={isVisible}
      >
        <span className="material-icons">help_outline</span>
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="status-flow-tooltip csv-import-tooltip"
          role="tooltip"
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          <div className="tooltip-header">
            {t('csvImportInfo.title')}
          </div>
          <div className="tooltip-content">
            <p className="csv-tooltip-description">
              {t(variant === 'multi' ? 'csvImportInfo.descriptionMulti' : 'csvImportInfo.descriptionSingle')}
            </p>
            <h5 className="csv-tooltip-section">{t('csvImportInfo.requiredFormat')}</h5>
            <ul className="csv-tooltip-list">
              <li>{t('csvImportInfo.separatorNote')}</li>
              <li>{t('csvImportInfo.quotesNote')}</li>
            </ul>
            <h5 className="csv-tooltip-section">{t('csvImportInfo.requiredColumns')}</h5>
            <ul className="csv-tooltip-list">
              <li>{t('csvImportInfo.productCode')}</li>
              {variant === 'multi' && <li>{t('csvImportInfo.serialNumber')}</li>}
              <li>{t('csvImportInfo.quantity')}</li>
            </ul>
            <p className="csv-tooltip-quantity-note">{t('csvImportInfo.quantityNote')}</p>
            <h5 className="csv-tooltip-section">{t('csvImportInfo.exampleCsv')}</h5>
            <pre className="csv-tooltip-pre">{exampleCsv}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default CsvImportTooltip;
